import { ShopStore } from "../../store/ShopStore.js";
import { shopUrl } from "../adventure/ActiveAdventure.js";
import type { Shop, ShopStockEntry } from "./shop.js";

/**
 * EV-E6 / I10 — the shop content loader `ShopStore` was waiting for.
 *
 * Shops are authored per `SHOP_AUTHORING.md` at
 * `public/<adventure>/data/shops/<shopkeeperId>.json`. At map load,
 * `main.tsx` collects the map's NPC ids and calls `loadShopsForNpcs`;
 * ids without a shop file 404 and are skipped silently (most NPCs don't
 * run shops), so `open_shop(<npc_id>)` from Ink finds the shop already
 * registered.
 */

/** Validate + normalize a parsed shop JSON. Returns null on shape errors. */
export function parseShopRecord(raw: unknown, shopkeeperId: string): Shop | null {
  if (typeof raw !== "object" || raw === null) return null;
  const rec = raw as {
    shopkeeperId?: unknown;
    stock?: unknown;
    buysTags?: unknown;
    buyPriceMultiplier?: unknown;
  };

  if (rec.shopkeeperId !== undefined && rec.shopkeeperId !== shopkeeperId) {
    console.warn(
      `[loadShops] ${shopkeeperId}.json declares shopkeeperId "${String(rec.shopkeeperId)}" — using the filename id`,
    );
  }

  const rawStock = Array.isArray(rec.stock) ? rec.stock : [];
  const stock: ShopStockEntry[] = [];
  for (const entry of rawStock) {
    if (typeof entry !== "object" || entry === null) return null;
    const e = entry as { itemId?: unknown; priceGold?: unknown; quantity?: unknown; restockOnCamp?: unknown };
    if (typeof e.itemId !== "string" || typeof e.priceGold !== "number" || e.priceGold < 0)
      return null;
    stock.push({
      itemId: e.itemId,
      priceGold: e.priceGold,
      quantity: typeof e.quantity === "number" ? e.quantity : Number.POSITIVE_INFINITY,
      restockOnCamp: e.restockOnCamp === true,
    });
  }

  const buysTags = Array.isArray(rec.buysTags)
    ? rec.buysTags.filter((t): t is string => typeof t === "string")
    : [];
  const buyPriceMultiplier =
    typeof rec.buyPriceMultiplier === "number" ? rec.buyPriceMultiplier : 0.5;

  return { shopkeeperId, stock, buysTags, buyPriceMultiplier };
}

/**
 * Fetch + register shop records for the given NPC ids. Missing files are
 * skipped silently; malformed files warn and are skipped. Returns the
 * number of shops registered.
 */
export async function loadShopsForNpcs(
  npcIds: readonly string[],
  fetchImpl: typeof fetch = fetch,
): Promise<number> {
  let registered = 0;
  await Promise.all(
    [...new Set(npcIds)].map(async (id) => {
      let raw: unknown;
      try {
        const res = await fetchImpl(shopUrl(id));
        if (!res.ok) return;
        raw = await res.json();
      } catch {
        return;
      }
      const shop = parseShopRecord(raw, id);
      if (!shop) {
        console.warn(`[loadShops] ${id}.json is malformed — shop skipped`);
        return;
      }
      ShopStore.register(shop);
      registered++;
    }),
  );
  return registered;
}
